<template>

    <div class="markdown-content" v-html="compiledMarkdown"></div>
</template>
<script setup>
import matter from 'gray-matter';
import { marked } from 'marked';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { COMPANY_PHONE, COMPANY_PHONE_RAW } from '@/lib/constants.js';

if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
    window.Buffer = {
        from: (value, encoding) => {
            if (encoding === 'utf8' || encoding === 'utf-8') {
                return new TextEncoder().encode(value);
            }
            return value;
        },
        isBuffer: (obj) => false
    };
}

const { locale } = useI18n();
const route = useRoute();
const compiledMarkdown = ref('');

/**
 * Replaces {{COMPANY_PHONE}} and {{COMPANY_PHONE_TEL}} tokens in markdown
 * with the values from constants.js, so phone numbers never need to be
 * hardcoded in markdown files.
 *
 * @param {string} md - Raw markdown string.
 * @returns {string} - Markdown with tokens resolved.
 */
function interpolateTokens(md) {
    return md
        .replaceAll('{{COMPANY_PHONE}}', COMPANY_PHONE)
        .replaceAll('{{COMPANY_PHONE_TEL}}', `tel:${COMPANY_PHONE_RAW}`);
}

/**
 * Preprocesses the markdown content to convert custom ID syntax into HTML.
 * This function replaces lines with the format "content {#id}" with "<p id="id">content</p>".
 *
 * @param {string} md - The markdown content to preprocess.
 * @returns {string} - The preprocessed markdown content.
 */
function preprocessMarkdown(md) {
    return md
        .split('\n')
        .map(line => {
            // Ignora encabezados, listas, y líneas vacías
            if (/^\s*#+\s/.test(line) || /^\s*[-*]\s/.test(line) || /^\s*$/.test(line)) {
                return line;
            }
            // Detecta {#id} y/o {.class} al final de una línea
            // Permite ambos, en cualquier orden, y múltiples clases
            const match = line.match(/^(.*?)(\s*\{[^}]+\})+\s*$/);
            if (match) {
                let content = match[1].trim();
                let id = '';
                let classes = [];
                // Extrae todos los bloques {...}
                const attrs = [...line.matchAll(/\{([^}]+)\}/g)].map(m => m[1]);
                attrs.forEach(attr => {
                    if (attr.startsWith('#')) {
                        id = attr.slice(1);
                    } else if (attr.startsWith('.')) {
                        // Puede haber varias clases separadas por punto
                        classes.push(...attr.slice(1).split('.').filter(Boolean));
                    }
                });
                let attrStr = '';
                if (id) attrStr += ` id=\"${id}\"`;
                if (classes.length) attrStr += ` class=\"${classes.join(' ')}\"`;
                return `<p${attrStr}>${content}</p>`;
            }
            return line;
        })
        .join('\n');
}


const loadMarkdown = async () => {
    try {
        // console.log('Loading legal markdown for:', route.params.id, 'in locale:', locale.value);
        const markdownModule = await import(`../../assets/markdowns/legal/${locale.value}/${route.params.id}.md?raw`);

        const rawMarkdown = markdownModule.default;
        const { content } = matter(rawMarkdown);
        const interpolated = interpolateTokens(content);
        const preprocessed = preprocessMarkdown(interpolated)

        compiledMarkdown.value = marked.parse(preprocessed)

    } catch (error) {
        console.error('Error loading legal markdown:', error);
        compiledMarkdown.value = `<div class="error">Error loading content: ${error.message}</div>`;
    }
};

onMounted(() => { loadMarkdown() });

watch([() => locale.value, () => route.params.id], () => {
    loadMarkdown();
});

</script>
<style>
.markdown-content h2 {
    font-size: 3.5rem !important;
    /* 35px */
    font-weight: 600;
    line-height: 1.2;
    margin: 1.5em 0 0.6em 0;
}

.markdown-content h3 {
    font-size: 3rem;
    /* 30px */
    font-weight: 600;
    line-height: 1.2;
    margin: 1.5em 0 0.8em 0;
}

.markdown-content .releasedate {
    font-size: 2rem;
    /* 20px */
    font-weight: 500;
    margin: 0 0 1em 0;
}

.markdown-content strong {
    font-weight: 500;
}

.markdown-content p,
.markdown-content ul>li {
    margin: 0 0 1.2em 0;
    line-height: 1.3;
    font-size: 2.8rem;
    /* 28px */
}

.markdown-content ul {
    padding-left: 4rem;
}

@media (max-width: 768px) {
    .markdown-content h2 {
        font-size: 2.8rem !important;
    }
    .markdown-content h3 {
        font-size: 2.4rem;
    }
    .markdown-content p,
    .markdown-content ul>li {
        font-size: 2.2rem;
    }
}

a {
    text-decoration: none;
    transition: color 0.5s ease, text-decoration 0.5s ease;
}

a:hover {
    text-decoration: underline;
    font-weight: 400;
}

.markdown-content {
    padding: 0;
}

</style>